<!--    Todo: change to correct component when updated to vue3-->
<template>
    <select
        v-model="localValue"
        class="form-control form-control-sm"
        :multiple="multiple"
        @change="change"
    >
        <option :value="null">{{ $t('general.select_option') }}</option>
        <option
            v-for="(option, index) of localOptions"
            :key="index"
            :value="option[optionIdKey]"
        >
            {{ option[optionLabelKey] }}
        </option>
    </select>
</template>

<script setup>
    import { onMounted, ref, watch } from 'vue';
    import { notifyError } from '../../utils/notify.js';
    import Axios from 'axios';

    const props = defineProps({
        value: {
            type: [String, Number, Array],
            default: null,
        },

        multiple: {
            type: Boolean,
            default: false,
        },

        options: {
            type: Array,
            default: null,
        },

        optionIdKey: {
            type: String,
            default: 'value',
        },

        optionLabelKey: {
            type: String,
            default: 'text',
        },

        url: {
            type: String,
            default: null,
        },

        placeholder: {
            type: String,
            default: null,
        },

        canCreate: {
            type: Boolean,
            default: false,
        },
    });

    const emit = defineEmits(['input', 'change']);

    const localOptions = ref(props.options ? [...props.options] : []);
    const localValue = ref(buildLocalValue(props.value));
    const loading = ref(false);

    watch(
        () => props.value,
        () => {
            localValue.value = buildLocalValue(props.value);

            if (props.url && !hasLoadedAllOptions()) {
                loadDefaultValues();
            }
        },
    );

    onMounted(() => {
        loadDefaultValues();
        search();
    });

    function hasLoadedAllOptions() {
        if (props.multiple) {
            return localValue.value.every(value => localOptions.value.findIndex(option => option[props.optionIdKey] === value) !== -1);
        }

        if (localValue.value === null || localValue.value === undefined) {
            return true;
        }

        return localOptions.value.findIndex(option => option[props.optionIdKey] === localValue.value) !== -1;
    }

    function buildLocalValue(value) {
        if (value === null) {
            return props.multiple ? [] : null;
        }

        if (!props.multiple) {
            return buildLocalItem(value);
        }

        return value.map(item => buildLocalItem(item));
    }

    function buildLocalItem(item) {
        if (item?.create) {
            return `|create|${item.create}`;
        }

        return item;
    }

    function buildValue(value) {
        if (props.multiple) {
            if (!localOptions.value) {
                return [];
            }

            return value.map(item => buildItem(item));
        }

        if (!localOptions.value) {
            return null;
        }

        const option = localOptions.value.find(option => option[props.optionIdKey] === value);

        if (!option) {
            return null;
        }

        return buildItem(option[props.optionIdKey]);
    }

    function buildItem(item) {
        if (String(item).substring(0, 8) === '|create|') {
            return {
                create: item.substring(8, item.length),
            };
        }

        return item;
    }

    async function search(query) {
        if (!props.url) {
            return;
        }

        loading.value = true;

        try {
            const response = await Axios.get(props.url, {
                params: {
                    search: query,
                },
            });

            localOptions.value = [...response.data];
        } catch (exception) {
            notifyError(exception);
        }

        loading.value = false;
    }

    async function loadDefaultValues() {
        if (!props.url) {
            return;
        }

        const values = props.multiple ? props.value || [] : [props.value];

        if (!values.filter(Boolean).length) {
            return;
        }

        loading.value = true;

        try {
            const response = await Axios.get(props.url, {
                params: {
                    values: values.join(','),
                },
            });

            localOptions.value = [...response.data];
        } catch (exception) {
            notifyError(exception);
        }

        loading.value = false;
    }

    function change() {
        const value = buildValue(localValue.value);
        emit('input', value);
        emit('change', value);
    }
</script>
